import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, ArrowRight, Pencil, Trash2, X, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const EMPTY = (type) => ({
  type, name: "", phone: "",
  emails: [""],
  address: "", opening_balance: 0, credit_period_days: 0, credit_rating: "", notes: "",
});

const partyEmails = (p) => {
  if (Array.isArray(p?.emails) && p.emails.length) return p.emails.filter(Boolean);
  if (p?.email) return [p.email];
  return [];
};

export default function Parties() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("customer");
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY("customer"));
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/parties?type=${tab}`);
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const startAdd = () => { setForm(EMPTY(tab)); setEditingId(null); setShowForm(true); };
  const startEdit = (p) => {
    const emails = partyEmails(p);
    setForm({
      type: p.type, name: p.name, phone: p.phone || "",
      emails: emails.length ? emails : [""],
      address: p.address || "", notes: p.notes || "",
      opening_balance: p.opening_balance || 0,
      credit_period_days: p.credit_period_days || 0,
      credit_rating: p.credit_rating || "",
    });
    setEditingId(p.id); setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditingId(null); setForm(EMPTY(tab)); };

  const updateEmail = (idx, val) => {
    const next = [...form.emails];
    next[idx] = val;
    setForm({ ...form, emails: next });
  };
  const addEmail = () => setForm({ ...form, emails: [...form.emails, ""] });
  const removeEmail = (idx) => {
    const next = form.emails.filter((_, i) => i !== idx);
    setForm({ ...form, emails: next.length ? next : [""] });
  };

  const submit = async (e) => {
    e.preventDefault();
    const cleanedEmails = (form.emails || []).map((s) => (s || "").trim()).filter(Boolean);
    const payload = {
      ...form,
      type: tab,
      emails: cleanedEmails,
      email: cleanedEmails[0] || "",   // legacy field kept in sync
      opening_balance: Number(form.opening_balance) || 0,
      credit_period_days: Number(form.credit_period_days) || 0,
    };
    try {
      if (editingId) {
        await api.patch(`/parties/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/parties", payload);
        toast.success("Added");
      }
      cancel(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}? Their transactions & ledger entries will remain but no new entries can be added against them.`)) return;
    try {
      await api.delete(`/parties/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="parties-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          <button onClick={() => { setTab("customer"); cancel(); }}
            className={`px-4 py-2 text-sm font-bold tracking-wider uppercase rounded-sm border ${tab === "customer" ? "bg-black text-white border-black" : "bg-white border-gray-300"}`}
            data-testid="tab-customer">Customers</button>
          {!isManager && (
            <button onClick={() => { setTab("vendor"); cancel(); }}
              className={`px-4 py-2 text-sm font-bold tracking-wider uppercase rounded-sm border ${tab === "vendor" ? "bg-black text-white border-black" : "bg-white border-gray-300"}`}
              data-testid="tab-vendor">Vendors</button>
          )}
        </div>
        {!showForm && (
          <button onClick={startAdd} className="btn-primary flex items-center gap-2" data-testid="add-party-btn">
            <Plus size={16} /> Add {tab === "customer" ? "Customer" : "Vendor"}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="card-flat p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="party-form">
          <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between">
            <div className="small-label text-gray-500">
              {editingId ? `Editing ${tab}` : `New ${tab}`}
            </div>
            <button type="button" onClick={cancel} className="text-gray-400 hover:text-black"><X size={18} /></button>
          </div>
          <div>
            <label className="small-label block mb-2">Name *</label>
            <input required className="input-lg" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="party-name" />
          </div>
          <div>
            <label className="small-label block mb-2">Phone</label>
            <input className="input-lg" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="party-phone" />
          </div>
          <div className="sm:col-span-2 lg:col-span-2">
            <label className="small-label block mb-2 flex items-center justify-between">
              <span>Emails</span>
              <button type="button" onClick={addEmail}
                className="text-[10px] font-bold tracking-wider uppercase text-gray-600 hover:text-black inline-flex items-center gap-1"
                data-testid="party-add-email">
                <Plus size={12} /> Add another
              </button>
            </label>
            <div className="space-y-2">
              {form.emails.map((em, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input type="email" className="input-lg flex-1" value={em}
                    onChange={(e) => updateEmail(idx, e.target.value)}
                    placeholder={idx === 0 ? "primary · invoices / reminders" : "additional contact email"}
                    data-testid={`party-email-${idx}`} />
                  {form.emails.length > 1 && (
                    <button type="button" onClick={() => removeEmail(idx)}
                      className="text-gray-400 hover:text-[#B91C1C] p-2"
                      data-testid={`party-remove-email-${idx}`}
                      title="Remove email">
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <label className="small-label block mb-2">Address</label>
            <input className="input-lg" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div>
            <label className="small-label block mb-2">Opening Balance ₹ (+ if they owe us)</label>
            <input type="number" step="0.01" className="input-lg" value={form.opening_balance}
              onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} data-testid="party-opening" />
          </div>
          {tab === "customer" && (
            <div>
              <label className="small-label block mb-2">Credit Period (days)</label>
              <input type="number" min="0" step="1" className="input-lg" value={form.credit_period_days}
                onChange={(e) => setForm({ ...form, credit_period_days: e.target.value })}
                placeholder="0 = cash, 15 = net-15, 30 = net-30…"
                data-testid="party-credit-days" />
            </div>
          )}
          {tab === "customer" && (
            <div>
              <label className="small-label block mb-2">Credit Rating</label>
              <select className="input-lg" value={form.credit_rating || ""}
                onChange={(e) => setForm({ ...form, credit_rating: e.target.value })}
                data-testid="party-credit-rating">
                <option value="">— not rated —</option>
                <option value="A">A · pays on time (no follow-up needed)</option>
                <option value="B">B · 2-3 follow-ups needed</option>
                <option value="C">C · multiple follow-ups needed</option>
                <option value="D">D · no response, personal visit required</option>
              </select>
            </div>
          )}
          <div className="lg:col-span-3">
            <label className="small-label block mb-2">Notes</label>
            <input className="input-lg" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="sm:col-span-2 lg:col-span-4 flex justify-end gap-2">
            <button type="button" onClick={cancel} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
            <button className="btn-primary" data-testid="party-submit">{editingId ? "Update" : "Save"}</button>
          </div>
        </form>
      )}

      <div className="card-flat">
        <div className="table-wrap">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Name</th>
              <th className="text-left">Contact</th>
              <th className="text-left">Address</th>
              <th className="num">Opening</th>
              {tab === "customer" && <th className="num">Credit</th>}
              <th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => {
              const emails = partyEmails(p);
              return (
              <tr key={p.id} data-testid={`party-row-${p.id}`}>
                <td className="font-semibold">{p.name}</td>
                <td className="text-xs">
                  {p.phone && <div className="data-num">{p.phone}</div>}
                  {emails.length > 0 && (
                    <div className="text-gray-500 break-all space-y-0.5" data-testid={`party-emails-${p.id}`}>
                      {emails.map((e, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <Mail size={10} className="text-gray-400 shrink-0" />
                          <span>{e}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!p.phone && emails.length === 0 && <span className="text-gray-400">—</span>}
                </td>
                <td className="text-sm text-gray-600">{p.address || "—"}</td>
                <td className="num">₹ {Number(p.opening_balance || 0).toFixed(2)}</td>
                {tab === "customer" && (
                  <td className="num text-xs text-gray-500">{p.credit_period_days ? `${p.credit_period_days}d` : "cash"}</td>
                )}
                <td className="text-right whitespace-nowrap">
                  <Link to={`/parties/${p.id}`} className="text-xs font-semibold inline-flex items-center gap-1 hover:underline mr-3" data-testid={`party-ledger-${p.id}`}>
                    Ledger <ArrowRight size={12} />
                  </Link>
                  <button onClick={() => startEdit(p)}
                    className="text-xs font-semibold text-gray-600 hover:text-black inline-flex items-center gap-1 mr-3"
                    data-testid={`party-edit-${p.id}`}>
                    <Pencil size={12} /> Edit
                  </button>
                  {isAdmin && (
                    <button onClick={() => remove(p)}
                      className="text-xs font-semibold text-[#B91C1C] hover:text-black inline-flex items-center gap-1"
                      data-testid={`party-del-${p.id}`}>
                      <Trash2 size={12} /> Delete
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
            {list.length === 0 && <tr><td colSpan="5" className="py-8 text-center text-gray-400">No {tab}s yet</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
